import {createApi} from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';

var urlPrefix = '';

switch(process.env.ENVIRONMENT_SETTINGS) {
case 'local':
    urlPrefix = 'http://localhost:8000';
    break;
case 'staging':
    urlPrefix = 'https://staging.docsihealth.com';
    break;
case 'demo':
    urlPrefix = 'https://demo.docsihealth.com';
    break;
case 'production':
    urlPrefix = 'https://api.docsihealth.com';
    break;
}

export const physicianDashboardSlice = createApi({
    reducerPath: 'dashboardApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${urlPrefix}/api`,
        prepareHeaders: (headers) => {
            headers.set('content-type', 'application/json');
            headers.set('accept', 'application/json');
            const token = localStorage.getItem('dashboardToken');
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            } else {
                console.warn('[Dashboard Auth] No dashboardToken found in localStorage when preparing request headers');
            }
            return headers;
        },
        credentials: 'include'
    }),
    tagTypes: ['Draft', 'UserDrafts', 'PhysicianCampaignDrafts', 'CampaignSummary', 'DashboardUser', 'CardOwner', 'ItemRecommendation', 'PhysicianCompliance', 'ComplianceRecommendation', 'Headline', 'Campaign', 'ItemReplacement'],
    endpoints: builder => ({
        getDraft: builder.query({
            query: (draftId) => `/drafts/${draftId}`,
            providesTags: (result, error, id) => [{ type: 'Draft', id: id }]
        }),
        updateDraft: builder.mutation({
            query: ({ draftId, status, physicianNotes, isCardMarkedForDeletion }) => ({
                url: `/drafts/${draftId}`,
                method: 'PATCH',
                body: {
                    status: status,
                    physician_notes: physicianNotes,
                    is_card_marked_for_deletion: isCardMarkedForDeletion
                }
            }),
            async onQueryStarted({ draftId }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedDraft } = await queryFulfilled;
                    dispatch(
                        physicianDashboardSlice.util.updateQueryData('getDraft', draftId.toString(), draft => {
                            Object.assign(draft, updatedDraft);
                        })
                    );
                } catch {
                    dispatch(NotificationActions.addErrorNotification({ message: 'Failed to update draft. Please try again later.' }));
                }
            },
            invalidatesTags: ['UserDrafts', 'PhysicianCampaignDrafts', 'DashboardUser', 'Headline', 'CampaignSummary']
        }),
        updateDraftComment: builder.mutation({
            query: ({ draftId, card, comment, commentText }) => ({
                url: `/drafts/${draftId}/comments/${comment.id}`,
                method: 'PATCH',
                body: {
                    body: commentText
                }
            }),
            async onQueryStarted({ draftId, card, comment, commentText }, {dispatch, queryFulfilled }) {
                const result = dispatch (
                    physicianDashboardSlice.util.updateQueryData('getDraft', draftId.toString(), draft => {
                        let existingComment = draft.comments.find(commentData => commentData.id === comment.id);
                        if (existingComment) {
                            existingComment.body = commentText
                        }
                    })
                )
                try {
                    await queryFulfilled;
                } catch {
                    result.undo();
                }
            },
        }),
        updateComplianceRecommendation: builder.mutation({
            query: ({ recommendation, status, rejectionContext }) => ({
                url: `drafts/${recommendation.draft.id}/recommendations/${recommendation.id}`,
                method: 'PATCH',
                body: {
                    status: status,
                    rejection_context: rejectionContext
                }
            }),
            async onQueryStarted({ recommendation, status, rejectionContext }, { dispatch, queryFulfilled }) {
                const result = dispatch (
                    physicianDashboardSlice.util.updateQueryData('getComplianceRecommendations', {'physicianId':recommendation.draft.card.owner.id.toString(), 'campaignId': recommendation.draft.campaign_id.toString()}, recommendations => {
                        let existingRec = recommendations.find(rec => rec.id === recommendation.id);
                        if (existingRec) { 
                            existingRec.status = status;
                            existingRec.rejection_context = rejectionContext;
                        }
                    })
                )
                try {
                    await queryFulfilled;
                } catch {
                    result.undo();
                }
            },
            invalidatesTags: ['DashboardUser', 'PhysicianCompliance', 'Headline', 'CampaignSummary'],
        }),
        updateItemSwapRecommendation: builder.mutation({
            query: ({ recId, status, physicianId, campaignId, rejectionContext }) => ({
                url: `itemRecommendations/${recId}`,
                method: 'PATCH',
                body: {
                    status: status,
                    rejection_context: rejectionContext
                }
            }),
            async onQueryStarted({ recId, status, physicianId, campaignId, rejectionContext }, {dispatch, queryFulfilled }) {
                const result = dispatch (
                    physicianDashboardSlice.util.updateQueryData('getPhysicianItemRecommendations', {'physicianId': physicianId, 'campaignId': campaignId}, recGroup => {
                        let existingRec = recGroup.find(rec => rec.id === recId);
                        if (existingRec) { 
                            existingRec.status = status;
                            existingRec.rejection_context = rejectionContext;
                        }
                    })
                )
                try {
                    await queryFulfilled;
                } catch {
                    result.undo();
                }
            },
            invalidatesTags: ['DashboardUser', 'Headline', 'CampaignSummary'],
        }),
        updateItemReplacementRecommendation: builder.mutation({
            query: ({ recommendationId, status, rejectionContext = [] }) => ({
                url: `itemRecommendations/${recommendationId}`,
                method: 'PATCH',
                body: {
                    status,
                    rejection_context: rejectionContext
                }
            }),
            async onQueryStarted({ cardOwnerId, draft, recommendationId, status, rejectionContext = [] }, { dispatch, queryFulfilled }) {
                const replacementResult = dispatch(
                    physicianDashboardSlice.util.updateQueryData('getItemReplacements', cardOwnerId, replacements => {
                        replacements.forEach((replacement) => {
                            const recommendation = replacement.recommendations?.find(({ id }) => String(id) === String(recommendationId));
                            if (recommendation) {
                                recommendation.status = status;
                                recommendation.rejection_context = rejectionContext;

                                const recommendations = replacement.recommendations ?? [];
                                const acceptedCount = recommendations.filter(({ status: s }) => s === 'accepted').length;
                                const rejectedCount = recommendations.filter(({ status: s }) => s === 'rejected').length;

                                if (acceptedCount === recommendations.length) {
                                    replacement.replacement_status = 'accepted';
                                } else if (rejectedCount === recommendations.length) {
                                    replacement.replacement_status = 'rejected';
                                } else if (acceptedCount > 0 || rejectedCount > 0) {
                                    replacement.replacement_status = 'partially_reviewed';
                                } else {
                                    replacement.replacement_status = 'open';
                                }
                            }
                        });
                    })
                );
                const draftResult = draft == null ? null : dispatch(
                    physicianDashboardSlice.util.updateQueryData('getDraft', draft.id.toString(), draftCache => {
                        Object.values(draftCache.recommendations ?? {}).forEach((recommendations) => {
                            const recommendation = recommendations.find(({ id }) => String(id) === String(recommendationId));
                            if (recommendation) {
                                recommendation.status = status;
                                recommendation.rejection_context = rejectionContext;
                            }
                        });
                    })
                );

                try {
                    const { data: updatedDraft } = await queryFulfilled;
                    if (draft) {
                        dispatch(
                            physicianDashboardSlice.util.updateQueryData('getDraft', draft.id.toString(), draftCache => {
                                Object.assign(draftCache, updatedDraft);
                            })
                        );
                    }
                } catch {
                    replacementResult.undo();
                    draftResult?.undo();
                }
            },
            invalidatesTags: (result, error, { recommendationId }) => [
                { type: 'ItemReplacement', id: recommendationId }
            ],
        }),
        getCurrentDashboardUser: builder.query({
            query: () => '/user',
            providesTags: ['DashboardUser']
        }),
        getCardOwner: builder.query({
            query: (cardOwnerId) => `/cardOwners/${cardOwnerId}`,
            providesTags: ['CardOwner']
        }),
        getTotalSavingsSummary: builder.query({
            query: ({ physicianId, campaignId }) => `/cardOwners/${physicianId}/savings?campaign=${campaignId}`,
        }),
        getPhysicianCampaignDrafts: builder.query({
            query: ({physicianId, campaignId}) => `/campaigns/${campaignId}/cardOwners/${physicianId}/drafts`,
            providesTags: ['PhysicianCampaignDrafts']
        }),
        getProcedureCampaignDrafts: builder.query({
            query: ({campaignId, procedureId}) => `/campaigns/${campaignId}/drafts?procedure_id=${procedureId}`,
            providesTags: ['ProcedureCampaignDrafts']
        }),
        getPhysicianItemRecommendations: builder.query({
            query: ({ physicianId, campaignId }) => `/cardOwners/${physicianId}/itemRecommendations?type=swap${campaignId ? `&campaign=${campaignId}` : ''}`,
            providesTags: (result = []) => [
                ...result.map(({ id }) => ({ type: 'ItemRecommendation', id }))
            ],
        }),
        getComplianceRecommendations: builder.query({
            query: ({ physicianId, campaignId }) => `/cardOwners/${physicianId}/itemRecommendations?type=compliance${campaignId ? `&campaign=${campaignId}` : ''}`,
            providesTags: (result = []) => [
                ...result.map(({ id }) => ({ type: 'ComplianceRecommendation', id }))
            ],
        }),
        getItemReplacements: builder.query({
            query: (cardOwnerId) => `/cardOwners/${cardOwnerId}/itemReplacements`,
            providesTags: (result = []) => [
                'ItemReplacement',
                ...result.map(({ item }) => ({ type: 'ItemReplacement', id: item.id }))
            ]
        }),
        getItemReplacementByRecommendationId: builder.query({
            query: (recommendationId) => `/itemRecommendations/${recommendationId}/itemReplacement`,
            providesTags: (result, error, recommendationId) => [{ type: 'ItemReplacement', id: recommendationId }]
        }),
        getHeadlineData: builder.query({
            query: ({ physicianId, campaignId }) => `/cardOwners/${physicianId}/headline${campaignId ? `?campaign=${campaignId}` : ''}`,
            providesTags: ['Headline']
        }),
        getPhysicianCompliance: builder.query({
            query: (physicianId) => `/cardOwners/${physicianId}/compliance`,
            providesTags: ['PhysicianCompliance']
        }),
        getDraftComparisons: builder.query({
            query: ({ draftId }) => `/drafts/${draftId}/comparisons`,
        }),
        logUserAction: builder.mutation({
            query: ({ action, data }) => ({
                url: '/userActions',
                method: 'POST',
                body: {
                    action: action,
                    data: data
                }
            }),
        }),
        getComparableItems: builder.query({
            query: ({ hospitalId, cardId, category, procedureId }) => ({
                url: `/hospitals/${hospitalId}/comparableItems?card=${cardId}&category=${category}&procedure=${procedureId}`,
                method: 'GET'
            }),
        }),
        resetPhysicianDashboardToken: builder.mutation({
            query: ({ phoneNumber }) => ({
                url: '/resetPhysicianDashboardToken',
                method: 'POST',
                body: {
                    phone_number: phoneNumber
                }
            }),
        }),
        getCampaignSummaries: builder.query({
            query: ({physicianId, status, maxDaysInPast}) => `/cardOwners/${physicianId}/campaigns${status ? `?status[]=${status}` : ''}${maxDaysInPast ? `&maxDaysInPast=${maxDaysInPast}` : ''}`,
            providesTags: ['CampaignSummary'],
        }),
        getCampaign: builder.query({
            query: (campaignId) => `/campaigns/${campaignId}`,
            providesTags: (result, error, arg) => [{ type: 'Campaign', id: arg }]
        }),
        sendItemImageVote: builder.mutation({
            query: ({ imageId, vote }) => ({
                url: `/itemImages/${imageId}/votes`,
                method: 'PUT',
                body: {
                    vote: vote
                }
            }),
        }),
        generateItemDetails: builder.query({
            query: (itemId) => `/items/${itemId}/generativeItemDetails`,
        }),
        sendGenerativeItemDetailVote: builder.mutation({
            query: ({ detailId, vote }) => ({
                url: `/generativeItemDetails/${detailId}/votes`,
                method: 'PUT',
                body: {
                    vote: vote
                }
            }),
        }),
        updateCardOwner: builder.mutation({
            query: ({ cardOwnerId, requestBody }) => ({
                url: `/cardOwners/${cardOwnerId}`,
                method: 'PATCH',
                body: requestBody
             }),
             async onQueryStarted({ cardOwnerId, requestBody }, { dispatch, queryFulfilled }) {
                const result = dispatch (
                    physicianDashboardSlice.util.updateQueryData('getCardOwner', cardOwnerId.toString(), cardOwner => {
                        Object.assign(cardOwner, requestBody);
                    })
                )
                try {
                    await queryFulfilled;
                } catch {
                    result.undo();
                    dispatch(NotificationActions.addErrorNotification({ message: 'Failed to update profile. Please try again later.' }));
                }
             },
             invalidatesTags: ['DashboardUser', 'CardOwner']
        }),
    })
});

export const {
    useGetDraftQuery,
    useUpdateDraftMutation,
    useUpdateDraftCommentMutation,
    useUpdateItemSwapRecommendationMutation,
    useUpdateItemReplacementRecommendationMutation,
    useGetCurrentDashboardUserQuery,
    useGetCardOwnerQuery,
    useGetTotalSavingsSummaryQuery,
    useGetPhysicianCampaignDraftsQuery,
    useGetProcedureCampaignDraftsQuery,
    useGetPhysicianItemRecommendationsQuery,
    useGetComplianceRecommendationsQuery,
    useGetItemReplacementsQuery,
    useGetItemReplacementByRecommendationIdQuery,
    useUpdateComplianceRecommendationMutation,
    useGetHeadlineDataQuery,
    useGetPhysicianComplianceQuery,
    useGetDraftComparisonsQuery,
    useLogUserActionMutation,
    useGetComparableItemsQuery,
    useResetPhysicianDashboardTokenMutation,
    useGetCampaignSummariesQuery,
    useGetCampaignQuery,
    useSendItemImageVoteMutation,
    useGenerateItemDetailsQuery,
    useSendGenerativeItemDetailVoteMutation,
    useUpdateCardOwnerMutation,
} = physicianDashboardSlice;
